import { CloseOutlined, CloudDownloadOutlined, FilePdfOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Checkbox, Collapse, Flex, Space, Tooltip, theme } from 'antd';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import type React from 'react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { downloadlabResultPdf } from '../../../../api/lab-result.queries.ts';
import { PdfPreviewModal } from '../../../../components/PdfPreviewModal.tsx';
import type { LabResultResponse, LabResultState } from '../../../../generated/openapi/befunde-api';
import { FemaleIcon } from '../../../../icons/FemaleIcon.tsx';
import { MaleIcon } from '../../../../icons/MaleIcon.tsx';
import { labResultStateColor, translateLabResultState } from '../../../../labresultstate/mapLabResultState.ts';
import { useLabResultViewStore } from '../../../../store/useLabResultViewStore.ts';
import { formatDateString, formatDateTimeString } from '../../../../utils/datetime.ts';
import { patientFullName } from '../../../../utils/patient.ts';

export const GenderIcon: FC<{ gender: string | null; className?: string }> = ({ gender, className }) => {
  switch (gender) {
    case 'M':
      return <MaleIcon className={className} />;
    case 'W':
      return <FemaleIcon className={className} />;
  }
  return null;
};

const personAge = (birthday: string): number => Math.round(dayjs().diff(birthday, 'years', true) * 10) / 10;

const DetailsRenderer: React.FC<{
  result: LabResultResponse;
  updatesAvailable: boolean;
}> = ({ result, updatesAvailable }) => {
  const { token } = theme.useToken();

  const subheaderClassName = css`
    color: ${token.colorTextDisabled};
    font-size: ${token.fontSizeSM}px;
    font-weight: bold;
    text-transform: uppercase;
  `;

  const hintClassName = css`
    color: ${token.blue5};
  `;

  return (
    <Flex wrap gap={token.paddingLG}>
      <div>
        <div className={subheaderClassName}>Geschlecht</div>
        <div>
          <GenderIcon
            gender={result.patient.gender}
            className={css`
              margin-right: ${token.marginXXS}px;
            `}
          />
          {result.patient.gender}
        </div>
      </div>
      <div>
        <div className={subheaderClassName}>Geburtsdatum</div>
        <div>{formatDateString(result.patient.birthdate)}</div>
      </div>
      <div>
        <div className={subheaderClassName}>Alter</div>
        <div>{personAge(result.patient.birthdate).toLocaleString('de-AT')} J</div>
      </div>
      <div>
        <div className={subheaderClassName}>SVNR</div>
        <div className={monoFont}>{result.patient.svnr}</div>
      </div>
      <div>
        <div className={subheaderClassName}>Auftragsnr.</div>
        <div className={monoFont}>{result.orderNumber}</div>
      </div>
      <div>
        <div className={subheaderClassName}>Eingangsdatum</div>
        <div>{formatDateTimeString(result.observationRequest.orderEntryDate)}</div>
      </div>
      <div>
        <div className={subheaderClassName}>Status</div>
        <div>{translateLabResultState(result.observationRequest.labResultStatus)}</div>
      </div>
      <div>
        <div className={subheaderClassName}>Stand</div>
        <div>{formatDateTimeString(result.timestamp)}</div>
        {updatesAvailable ? (
          <Space className={hintClassName}>
            <WarningOutlined />
            Neue Version verfügbar
          </Space>
        ) : null}
      </div>
    </Flex>
  );
};

const useResultDetailsStyles = (labResultStatus: LabResultState) => {
  const { token } = theme.useToken();

  return useMemo(
    () => ({
      collapse: css`
        border-left: 5px solid ${labResultStateColor(token, labResultStatus)};
        .ant-collapse-header {
          line-height: 1.5em !important;
        }
        .ant-collapse-header-text {
          font-size: 1.4em;
          font-weight: bold;

          @media (max-width: ${token.screenLG}px) {
            font-size: 1.2em;
          }
        }
      `,
    }),
    [token, labResultStatus]
  );
};

export const BefundDetailsHeader: FC<{
  result: LabResultResponse;
  refetch: (() => void) | null;
}> = ({ result, refetch }) => {
  const { token } = theme.useToken();
  const styles = useResultDetailsStyles(result.observationRequest.labResultStatus);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { message } = App.useApp();
  const selectedId = useLabResultViewStore(state => state.selectedId);
  const cumulative = useLabResultViewStore(state => state.cumulative);
  const switchCumulative = useLabResultViewStore(state => state.switchCumulative);

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');

  const pdfFileName = `befund-${result.orderNumber}-${result.timestamp}.pdf`;

  // Opens the full viewer instead of writing the file straight to disk. Downloading is
  // still one click away, from inside the dialog.
  const openPdf = async () => {
    setPdfLoading(true);

    try {
      // biome-ignore lint/style/noNonNullAssertion: should be set
      const response = await downloadlabResultPdf({ orderResultId: selectedId! });

      if (response.status !== 200) {
        message.error('Es ist ein Fehler aufgetreten');
        return;
      }

      // Wrapped rather than used as-is: the generated client types the body loosely, and
      // the object URL needs a real Blob with the right type.
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfBlob(blob);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.error(e);
      message.error('Es ist ein Fehler aufgetreten');
    } finally {
      setPdfLoading(false);
    }
  };

  // Releases the previous URL whenever it changes and on unmount, so navigating away with
  // the dialog open does not strand a multi-megabyte blob for the life of the page.
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const closePdf = () => {
    setPdfUrl('');
    setPdfBlob(null);
  };

  return (
    <>
      <Collapse
        // bordered={false}
        defaultActiveKey={window.innerWidth < token.screenMD ? [] : ['details']}
        className={styles.collapse}
        items={[
          {
            key: 'details',
            label: (
              <Flex align="center" gap="middle" wrap justify="space-between">
                <div style={{ fontWeight: 'bold' }}>{patientFullName(result.patient)}</div>
                <Flex gap="small" wrap align="center">
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      switchCumulative();
                    }}
                    style={{ fontWeight: 'normal' }}
                  >
                    <Checkbox checked={cumulative}>Kumulativ Ansicht</Checkbox>
                  </span>
                  <Button
                    size="small"
                    type="primary"
                    icon={<FilePdfOutlined />}
                    loading={pdfLoading}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      openPdf();
                    }}
                  >
                    PDF
                  </Button>
                  {refetch ? (
                    <Tooltip title="Neue Version verfügbar">
                      <Button
                        color="blue"
                        variant="filled"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          refetch();
                        }}
                      >
                        Neue Version laden
                      </Button>
                    </Tooltip>
                  ) : null}
                </Flex>
              </Flex>
            ),
            children: <DetailsRenderer result={result} updatesAvailable={!!refetch} />,
          },
        ]}
      />
      <PdfPreviewModal
        title={`Befund ${result.orderNumber}`}
        url={pdfUrl}
        loading={pdfLoading}
        onClose={closePdf}
        footer={
          <Space>
            <Button icon={<CloseOutlined />} onClick={closePdf}>
              Schließen
            </Button>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              disabled={!pdfBlob}
              onClick={() => pdfBlob && saveAs(pdfBlob, pdfFileName)}
            >
              Herunterladen
            </Button>
          </Space>
        }
      />
    </>
  );
};
